import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  forwardRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormBuilder,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import phone from 'phone';
import {
  COUNTRY_CONFIGS,
  COUNTRY_CONFIG_BY_COUNTRY,
  COUNTRY_CONFIG_BY_SHORT_DISPLAY,
} from './phone-number-configs';

@Component({
    selector: 'phone-number-input',
    templateUrl: 'phone-number-input.component.html',
    styleUrl: 'phone-number-input.component.scss',
    imports: [
        FormsModule,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        ReactiveFormsModule,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PhoneNumberInput),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhoneNumberInput implements ControlValueAccessor {
  private readonly fb = inject(FormBuilder);

  protected readonly countryConfigs = COUNTRY_CONFIGS;

  protected countryCode = 1;
  protected phoneNumber = '';

  protected form = this.fb.group({
    country: this.fb.nonNullable.control<string>(
      COUNTRY_CONFIGS[0].shortDisplay,
    ),
    phoneNumber: this.fb.nonNullable.control<string>(''),
  });
  protected onChange: (value: string) => void = () => {};
  protected onTouched: () => void = () => {};

  @ViewChild('phoneInput', { static: true, read: ElementRef })
  private readonly phoneInput!: ElementRef;

  constructor() {
    this.form.controls.country.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((country) => {
        if (!COUNTRY_CONFIG_BY_SHORT_DISPLAY.has(country)) {
          this.form.controls.country.setValue(COUNTRY_CONFIGS[0].shortDisplay, {
            emitEvent: false,
          });
          return;
        }

        this.countryCode = COUNTRY_CONFIG_BY_SHORT_DISPLAY.get(country)!.code;
        this.emit();
      });

    this.form.controls.phoneNumber.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((phoneNumber) => {
        this.phoneNumber = phoneNumber;
        this.emit();
      });
  }

  writeValue(value: string): void {
    if (!value) {
      this.form.setValue({
        country: COUNTRY_CONFIGS[0].shortDisplay,
        phoneNumber: '',
      });
    }
    const number = phone(value);

    let country = number.countryIso2 ?? 'US';
    if (!COUNTRY_CONFIG_BY_COUNTRY.has(country)) {
      country = 'US';
    }
    this.form.setValue({
      country: COUNTRY_CONFIG_BY_COUNTRY.get(country)!.shortDisplay,
      phoneNumber: `${number.phoneNumber ?? ''}`,
    });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  emit() {
    this.onChange(`+${this.countryCode} ${this.phoneNumber}`);
  }

  focus() {
    this.phoneInput.nativeElement.focus();
  }
}
